<template>
  <div class="space-y-24">
    <RevLoadingScreen
      v-if="isLoading"
      class="mt-8"
      :text="i18n(translations.loading)"
    />
    <template v-else>
      <Banner />
      <ReferenceMarket
        :key="sellerStore.referenceMarket"
        :reference-market="sellerStore.referenceMarket ?? ''"
        @change-market="handleReferenceMarketUpdate"
      />
      <ul class="space-y-24">
        <li
          v-for="market in markets"
          :key="market"
          :data-qa="`markets-list-market-${market}`"
        >
          <MarketPricingRule
            :country-code="market"
            :metadata="getMetadataForMarket(market)"
            :pricing-rules="getMarketPricingRules(market)"
            @update="handleOnUpdate"
          />
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  HttpApiError,
  catalogAccessAPI,
  merchantsAPI,
} from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { PricingRule } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import translations from './Pricing.translations'
import type { FormattedPricingRuleMetadata } from './Pricing.types'
import Banner from './components/Banner/Banner.vue'
import MarketPricingRule from './components/MarketPricingRule/MarketPricingRule.vue'
import ReferenceMarket from './components/ReferenceMarket/ReferenceMarket.vue'

const i18n = useI18n()
const sellerStore = useSellerInfoStore()
const { openErrorToast } = useTheToast()

const { data: metadata, pending: isMetadataPending } = useHttpFetch(
  merchantsAPI.getPricingRulesMetadata,
)
const { data: configuration, pending: isConfigurationPending } = useHttpFetch(
  merchantsAPI.getMerchantConfiguration,
)
const { data: categories, pending: isCategoriesPending } = useHttpFetch(
  catalogAccessAPI.getCategoriesLeafLight,
  { queryParams: { my_categories: true } },
)

const {
  data: pricingRules,
  refresh: refreshPricingRules,
  pending: isPricingRulesPending,
  error,
} = useHttpFetch(merchantsAPI.getPricingRules)

if (error.value) {
  openErrorToast({
    content: HttpApiError.fromAnyError(error.value).title || '',
  })
}

const markets = computed(() => {
  return (
    sellerStore.activeMarkets
      .filter((market) => market !== sellerStore.referenceMarket)
      // sort alphabetically by country name (and not country code)
      .sort((a, b) => i18n.country(a).localeCompare(i18n.country(b)))
  )
})

const isLoading = computed(() => {
  return (
    isPricingRulesPending.value ||
    isConfigurationPending.value ||
    isMetadataPending.value ||
    isCategoriesPending.value
  )
})

async function handleOnUpdate() {
  await refreshPricingRules()
}

function getMarketPricingRules(market: MarketCountryCode): PricingRule[] {
  return (
    pricingRules.value?.results.filter(
      (pricingRule: PricingRule) => pricingRule.targetMarket === market,
    ) || []
  )
}

function getMetadataForMarket(
  market: MarketCountryCode,
): FormattedPricingRuleMetadata {
  return {
    categories: categories.value?.results || [],
    banner: metadata.value?.banners.find((banner) => banner.market === market)
      ?.banner,
    fixedMultiplier: metadata.value?.fixedMultipliers.find(
      (fixedMultiplier) => fixedMultiplier.market === market,
    )?.value,
    withoutBackboxOnlyDisabledMarkets:
      metadata.value?.withoutBackboxOnlyDisabledMarkets.includes(market) ||
      false,
    automaticUpdatesEnabled: configuration.value?.markets.find(
      (marketConfiguration) => marketConfiguration.market === market,
    )?.automaticUpdatesEnabled,
    updateOnlyListingsWithoutBackbox: configuration.value?.markets.find(
      (marketConfiguration) => marketConfiguration.market === market,
    )?.updateOnlyListingsWithoutBackbox,
  }
}

function handleReferenceMarketUpdate() {
  sellerStore.fetchSellerInfo()
  refreshPricingRules()
}
</script>
